import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { AlertCircle, CheckCircle2, ArrowLeft, Loader2 } from "lucide-react";

const AuthCallback = () => {
  const navigate = useNavigate();
  const [message, setMessage] = useState("Verifying your email...");
  const [isError, setIsError] = useState(false);
  const [showResendHint, setShowResendHint] = useState(false);

  useEffect(() => {
    const runVerification = async () => {
      const searchParams = new URLSearchParams(window.location.search);
      const hash = window.location.hash.startsWith("#") ? window.location.hash.slice(1) : window.location.hash;
      const hashParams = new URLSearchParams(hash);

      const errorCode = searchParams.get("error_code") || hashParams.get("error_code") || "";
      const errorDescription = decodeURIComponent(
        searchParams.get("error_description") || hashParams.get("error_description") || ""
      );
      const combinedError = `${errorCode} ${errorDescription}`.toLowerCase().trim();

      const code = searchParams.get("code");
      const token = searchParams.get("token");
      const tokenHash = searchParams.get("token_hash");
      const type = searchParams.get("type") || hashParams.get("type") || "signup";

      try {
        if (combinedError.length > 0) {
          if (combinedError.includes("expired") || combinedError.includes("otp_expired")) {
            setIsError(true);
            setShowResendHint(true);
            setMessage("Verification link has expired");
          } else if (combinedError.includes("already") || combinedError.includes("used")) {
            setIsError(false);
            setMessage("This verification link was already used, but your account is verified. Redirecting to login...");
            setTimeout(() => navigate("/login", { replace: true }), 1500);
          } else {
            setIsError(true);
            setMessage("Invalid verification link. Account activation denied.");
          }
          return;
        }

        if (code) {
          const { error } = await supabase.auth.exchangeCodeForSession(code);
          if (error) throw error;
        } else if (tokenHash && type) {
          const { error } = await supabase.auth.verifyOtp({
            type,
            token_hash: tokenHash,
          });
          if (error) throw error;
        } else if (token) {
          const { error } = await supabase.auth.verifyOtp({
            type,
            token,
          });
          if (error) throw error;
        }

        setIsError(false);
        setMessage("Email verified successfully. Redirecting to login...");
        await supabase.auth.signOut();
        setTimeout(() => navigate("/login", { replace: true }), 1500);
      } catch (err) {
        const normalized = String(err.message || err.error_description || err.status || "").toLowerCase();

        if (normalized.includes("expired") || normalized.includes("otp_expired")) {
          setIsError(true);
          setShowResendHint(true);
          setMessage("Verification link has expired");
        } else if (normalized.includes("already") || normalized.includes("used")) {
          // Link was already used - check if account is actually verified
          setIsError(false);
          setMessage("This verification link was already used, but your account is verified. Redirecting to login...");
          setTimeout(() => navigate("/login", { replace: true }), 1500);
        } else {
          setIsError(true);
          setMessage("Unable to verify email. Please try resending the verification link or contact support.");
        }
      }
    };

    runVerification();
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#f3f2f1] px-6">
      <div className="w-full max-w-md bg-white p-10 rounded-[2.5rem] shadow-2xl border border-gray-100 text-center">
        <button
          type="button"
          onClick={() => navigate("/login")}
          className="mb-6 inline-flex items-center gap-2 text-xs font-black uppercase tracking-wide text-gray-600 hover:text-brand-maroon"
        >
          <ArrowLeft size={14} /> Back to Login
        </button>

        <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 ${isError ? "bg-red-50 text-red-500" : "bg-green-50 text-green-500"}`}>
          {isError ? <AlertCircle size={40} /> : <CheckCircle2 size={40} />}
        </div>

        <h1 className="text-2xl font-black text-gray-900 mb-4 tracking-tighter italic uppercase">Email Verification</h1>

        <div className={`mb-6 p-4 rounded-2xl text-sm font-semibold ${isError ? "bg-red-50 text-red-700" : "bg-green-50 text-green-700"}`}>
          {message}
        </div>

        {showResendHint ? (
          <button
            type="button"
            onClick={() => navigate("/login", { replace: true })}
            className="w-full bg-brand-maroon text-white py-4 rounded-full text-xs font-black uppercase tracking-widest hover:bg-red-700 transition shadow-lg"
          >
            Go to Login and Resend
          </button>
        ) : (
          <div className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-wide text-gray-600">
            <Loader2 size={14} className="animate-spin" /> Redirecting...
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;